import React from 'react';
import { useSelector } from 'react-redux';
import { MathUtils, Point } from '@printu/geom';
import { getItemAbsolutePoints } from 'app/core/markers/bounds';
import { getProjectState } from 'app/stores/project';
import { ProjectActions } from 'app/stores/project/actions';
import { useAppDispatch } from 'app/stores/store';
import { Edit } from './Edit';
const HandleIds = {
    start: 'start',
    end: 'end',
    zoom: 'zoom',
    size: 'size',
};
const Consts = {
    zoomAnchorAngleOffset: 35,
    zoomAnchorAngleRange: 90,
    zoomMaxValue: 3,
};
const getVectorAngle = (vec1, vec2) => {
    const angleRad = Point.angle(vec1.normalized(1), vec2.normalized(1));
    return angleRad < 0 ? 2 * Math.PI + angleRad : angleRad;
};
const getRadiusAnchor = (marker, transform = point => point) => {
    const [start, end] = getItemAbsolutePoints(marker);
    const { width, height } = marker;
    const radius = Math.min(width * 0.5, height * 0.5);
    return end.add(transform(start.subtract(end).normalize(radius)));
};
const getZoomAnchor = (marker) => {
    const unifiedZoom = ((marker.zoom - 1) / (Consts.zoomMaxValue - 1)) *
        Consts.zoomAnchorAngleRange;
    return getRadiusAnchor(marker, localAnchor => localAnchor.rotate(-MathUtils.degToRad(Consts.zoomAnchorAngleOffset + unifiedZoom)));
};
const getMarkerSizeCPt = (marker) => {
    switch (marker.type) {
        case 'rect': {
            const [, end] = getItemAbsolutePoints(marker);
            const { width, height } = marker;
            return end.add(Point.from(width * 0.5, height * 0.5));
        }
        case 'ellipse':
        default:
            return getRadiusAnchor(marker);
    }
};
const createHandles = (marker) => {
    const [start, end] = getItemAbsolutePoints(marker);
    return {
        [HandleIds.start]: {
            current: start.toObject(),
        },
        [HandleIds.end]: {
            current: end.toObject(),
        },
        [HandleIds.size]: {
            current: getMarkerSizeCPt(marker).toObject(),
        },
        [HandleIds.zoom]: {
            current: getZoomAnchor(marker).toObject(),
        },
    };
};
export const EditZoom = () => {
    const marker = useSelector(state => {
        const project = getProjectState(state);
        const selectedItemId = project.selection[0];
        if (!selectedItemId) {
            return undefined;
        }
        return project.markers[selectedItemId];
    });
    const dispatch = useAppDispatch();
    const onHandleChanged = React.useCallback((handleId, point, _downPoint, initial) => {
        if (!marker) {
            return undefined;
        }
        let patch;
        const { id } = marker;
        const [start, end] = getItemAbsolutePoints(marker);
        switch (handleId) {
            case HandleIds.start:
                patch = {
                    id,
                    patch: {
                        x: point.virtual.x,
                        y: point.virtual.y,
                        endX: end.x - point.virtual.x,
                        endY: end.y - point.virtual.y,
                    },
                };
                break;
            case HandleIds.end:
                patch = {
                    id,
                    patch: {
                        endX: point.virtual.x - start.x,
                        endY: point.virtual.y - start.y,
                    },
                };
                break;
            case HandleIds.size:
                switch (marker.type) {
                    case 'rect':
                        {
                            const newWidth = Math.max(30, (point.virtual.x - end.x) * 2);
                            const newHeight = Math.max(30, (point.virtual.y - end.y) * 2);
                            patch = {
                                id,
                                patch: {
                                    width: newWidth,
                                    height: newHeight,
                                },
                            };
                        }
                        break;
                    case 'ellipse':
                    default:
                        {
                            const maxRadius = 0.6 * Point.distance(start, end);
                            const absDistance = Math.abs(Point.distance(end, point.virtual));
                            const newRadius = MathUtils.clamp(absDistance, 30, maxRadius);
                            patch = {
                                id,
                                patch: {
                                    width: 2 * newRadius,
                                    height: 2 * newRadius,
                                },
                            };
                        }
                        break;
                }
                break;
            case HandleIds.zoom:
                {
                    const anchorBase = getRadiusAnchor(marker, localAnchor => localAnchor.rotate(-MathUtils.degToRad(Consts.zoomAnchorAngleOffset)));
                    const angle = getVectorAngle(anchorBase.subtract(end), new Point(point.virtual.x - end.x, point.virtual.y - end.y));
                    const maxAngle = MathUtils.degToRad(Consts.zoomAnchorAngleRange);
                    if (angle > 0 && angle < maxAngle) {
                        const zoomAngleMax = MathUtils.clamp(angle, 0, maxAngle);
                        const zoomMaxRange = (Consts.zoomMaxValue - 1) * zoomAngleMax;
                        const newZoom = 1 + zoomMaxRange / maxAngle;
                        patch = {
                            id,
                            patch: {
                                zoom: newZoom,
                            },
                        };
                    }
                }
                break;
            default:
                break;
        }
        if (patch) {
            dispatch(ProjectActions.itemUpdate(Object.assign(Object.assign({}, patch), { undoable: initial })));
        }
        return undefined;
    }, [marker, dispatch]);
    return (React.createElement(Edit, { handles: createHandles(marker), onHandleChanged: onHandleChanged }));
};
